import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import MapWithOutSidebar from "../components/MapWithOutSidebar";
import Header from "../components/Header";
import HeaderAbove from "../components/HeaderAbove";
import FilterPanel from "../components/FilterPanel";
import BuscarPanel from "../components/BuscarPanel";
import mercados from "../services/mercadosService";
import { buildLayerWhere } from "../utils/filterUtils";
import { COLORS_MERCADOS } from "../utils/styleUtils";

const PARQUES_URL = process.env.REACT_APP_PARQUES_INDUSTRIALES;
const ZONAS_URL = process.env.REACT_APP_ZONAS_INDUSTRIALES;

// Computa el set completo de pares (estado, modalidad) válidos para mercados.
// Cada marker pertenece a un par específico. Filtering por pares unifica el
// control del FilterPanel (chips de estado) y de la Legend (clicks de modalidad).
const ALL_MERCADO_PAIRS = (() => {
  const out = new Set();
  Object.entries(COLORS_MERCADOS).forEach(([estadoId, estado]) => {
    Object.keys(estado.modalidades || {}).forEach((modId) => {
      out.add(`${estadoId}-${modId}`);
    });
  });
  return out;
})();

const THEME_KEY = "mici-theme";

const Map = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isBuscarOpen, setIsBuscarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filteredData, setFilteredData] = useState(null);
  // Estado raw de filtros parques/zonas — objeto con keys de filterConfig
  // (disponibilidad/uso/etc.) más __text y __area opcionales. Cada wrapper
  // de sublayer construye su propio WHERE en base a este state vía
  // buildLayerWhere. Mantiene el nombre legacy `arcgisWhereClause` para no
  // tocar todas las refs en el árbol; ahora es un objeto, no un string.
  const [arcgisWhereClause, setArcgisWhereClause] = useState({});
  const [centroide, setCentroide] = useState([]);
  const [selectedLayerIds, setSelectedLayerIds] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    const saved = window.localStorage.getItem(THEME_KEY);
    return saved === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch (e) {
      /* localStorage no disponible */
    }
  }, [theme]);

  // Marcamos el body con `has-filter-open` cuando el FilterPanel está
  // abierto. El CSS usa esa clase para ocultar el control de zoom de
  // Leaflet (arriba a la izquierda) que se superpone con el panel.
  useEffect(() => {
    if (isFilterOpen) {
      document.body.classList.add("has-filter-open");
    } else {
      document.body.classList.remove("has-filter-open");
    }
    return () => {
      document.body.classList.remove("has-filter-open");
    };
  }, [isFilterOpen]);

  // ============================================================
  // Contadores por tab — reflejan resultados visibles en el mapa
  // ============================================================
  const [mercadosTotal, setMercadosTotal] = useState(null);
  const [parquesCount, setParquesCount] = useState(null);
  const [zonasCount, setZonasCount] = useState(null);

  // Total de mercados minoristas — fetch una vez al cargar la app
  useEffect(() => {
    mercados
      .getMercadosMinoristas()
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        const validCount = arr.filter((item) => {
          const pairKey = `${item.id_estado_intervencion}-${item.id_modalidad}`;
          return ALL_MERCADO_PAIRS.has(pairKey);
        }).length;
        setMercadosTotal(validCount);
      })
      .catch(() => setMercadosTotal(0));
  }, []);

  // Cache de counts por (baseUrl, layerId, where). El distinct count por capa
  // no cambia durante la sesión (data estática) — así toggles de checkboxes
  // que solo cambian el set de layers visibles reutilizan el resultado en
  // memoria sin volver a llamar al ArcGIS. Una request por combinación nueva
  // de where; tantas toggles como quieras después son instantáneas.
  //
  // Usamos un objeto plano (no `new Map()`) porque el componente se llama
  // `Map` y sombrea al constructor nativo en este scope.
  const countCache = useRef({});

  // Cuenta id_intervencion distintos en una sublayer, **descartando features
  // sin geometría válida**. ArcGIS a veces tiene registros "fantasma" (p.ej.
  // un parque industrial llamado "sssss" con id=846 que se grabó sin shape):
  // antes los contábamos porque `returnDistinctValues=true&returnGeometry=
  // false` solo trae los ids, no la geometría — el badge subía pero el mapa
  // no pintaba nada para ese registro. Ahora pedimos la geometría y filtramos
  // los que tienen rings/paths vacíos (o `null`) antes de contar.
  const fetchDistinctCount = useCallback((baseUrl, layerId, where) => {
    const key = `${baseUrl}|${layerId}|${where}`;
    if (key in countCache.current) {
      return countCache.current[key];
    }
    const url =
      `${baseUrl}/${layerId}/query?where=${encodeURIComponent(where)}` +
      `&outFields=id_intervencion&returnGeometry=true&f=json`;
    const promise = fetch(url)
      .then((r) => r.json())
      .then((d) => {
        const features = Array.isArray(d?.features) ? d.features : [];
        const validIds = new Set();
        features.forEach((f) => {
          const g = f?.geometry;
          const hasGeom =
            (Array.isArray(g?.rings) && g.rings.length > 0) ||
            (Array.isArray(g?.paths) && g.paths.length > 0) ||
            (typeof g?.x === "number" && typeof g?.y === "number");
          if (!hasGeom) return;
          const id = f?.attributes?.id_intervencion;
          if (id != null) validIds.add(id);
        });
        return validIds.size;
      })
      .catch(() => {
        // No cachear errores — permite reintento en el siguiente cambio
        delete countCache.current[key];
        return 0;
      });
    countCache.current[key] = promise;
    return promise;
  }, []);

  // Helper: cuenta features sumando todas las sublayers visibles, deduplicando
  // por `id_intervencion` dentro de cada layer. Algunos features están repetidos
  // en el feature service (p.ej. ZED Zofratacna aparece 2 veces con el mismo
  // id_intervencion=425) y se ven como un marker overlappeado en el mapa, lo
  // cual hacía que el contador mostrara 5 cuando el usuario veía 4.
  //
  // Usamos `returnDistinctValues=true` sobre `id_intervencion` — el servidor
  // hace el distinct y nos devuelve solo los valores únicos. Bypassa el
  // maxRecordCount (1000) que tenía `returnIdsOnly`.
  //
  // No deduplicamos entre layers: cada sublayer renderiza visualmente diferente
  // (marker, polígono, lote, etc.), así que sumamos sus distinct counts.
  // Acepta una función `getWhereFor(layerId)` para que cada layer use su
  // propio WHERE (per-layer filtering). Esto permite que un filtro como
  // "disponibilidad = 1" solo aplique a la sublayer que tiene `id_ocupacion`
  // (parques /4), sin afectar las otras.
  const countAcrossLayers = useCallback(
    (baseUrl, layerIds, getWhereFor) => {
      if (!baseUrl || !Array.isArray(layerIds) || layerIds.length === 0) {
        return Promise.resolve(0);
      }
      return Promise.all(
        layerIds.map((id) =>
          fetchDistinctCount(baseUrl, id, getWhereFor(id))
        )
      ).then((counts) => counts.reduce((a, b) => a + b, 0));
    },
    [fetchDistinctCount]
  );

  // Capas default para cada MapServer — los mismos índices que mappea
  // FilterPanel cuando los checkboxes están en su estado inicial. Los usamos
  // para que los badges de Parques/Zonas tengan un total razonable cuando el
  // usuario no está parado en ese tab (porque `selectedLayerIds` se resetea a
  // [] al cambiar de tab).
  const PARQUES_DEFAULT_LAYERS = useMemo(() => [0, 1, 2], []);
  const ZONAS_DEFAULT_LAYERS = useMemo(() => [2], []);

  // El badge de Parques solo cuenta ZED (/1) + Parques Industriales (/2). Los
  // toggles de Zonificación (/3) y Unidades Inmobiliarias (/4) afectan el mapa
  // pero no el contador, porque son sub-entidades de los parques y los
  // duplicarían visualmente.
  const COUNTABLE_PARQUES = useMemo(() => [1, 2], []);

  // Contador para parques — si el usuario está en el tab de parques, refleja
  // los toggles del panel "Capas a mostrar"; en otros tabs, usa el set default
  // para mostrar el total potencial.
  //
  // Nota: el MapServer expone un esquema reducido (`id_intervencion`,
  // `valor_descripcion`...) sin los campos del filtro de UI (tipo_convenio,
  // disponibilidad, uso, id_ubigeo). Cuando el WHERE los referencia, ArcGIS
  // devuelve `error` y `countAcrossLayers` cuenta 0 para esa sublayer — el
  // badge baja a 0 (indica "los filtros aplicados no matchean nada en esta
  // capa", que es lo correcto dado que la capa no tiene esos campos).
  useEffect(() => {
    if (!PARQUES_URL) return;
    const filters =
      typeof arcgisWhereClause === "object" && arcgisWhereClause
        ? arcgisWhereClause
        : {};
    const visibleLayers =
      activeTab === 2 ? selectedLayerIds : PARQUES_DEFAULT_LAYERS;
    const layers = visibleLayers.filter((id) =>
      COUNTABLE_PARQUES.includes(id)
    );
    countAcrossLayers(PARQUES_URL, layers, (id) =>
      buildLayerWhere("parquesIndustriales", id, filters)
    ).then(setParquesCount);
  }, [
    arcgisWhereClause,
    activeTab,
    selectedLayerIds,
    countAcrossLayers,
    PARQUES_DEFAULT_LAYERS,
    COUNTABLE_PARQUES,
  ]);

  // Contador para zonas industriales — mismo razonamiento.
  useEffect(() => {
    if (!ZONAS_URL) return;
    const filters =
      typeof arcgisWhereClause === "object" && arcgisWhereClause
        ? arcgisWhereClause
        : {};
    const layers =
      activeTab === 3 ? selectedLayerIds : ZONAS_DEFAULT_LAYERS;
    countAcrossLayers(ZONAS_URL, layers, (id) =>
      buildLayerWhere("zonasIndustriales", id, filters)
    ).then(setZonasCount);
  }, [
    arcgisWhereClause,
    activeTab,
    selectedLayerIds,
    countAcrossLayers,
    ZONAS_DEFAULT_LAYERS,
  ]);

  const tabCounts = useMemo(() => {
    // Tab 0 (Mercados): cuando hay filtros locales activos sobre este tab,
    // filteredData refleja los resultados visibles; si no, mostramos total.
    const tab0 =
      activeTab === 0 && Array.isArray(filteredData)
        ? filteredData.length
        : mercadosTotal;

    return {
      0: tab0,
      2: parquesCount,
      3: zonasCount,
    };
  }, [activeTab, filteredData, mercadosTotal, parquesCount, zonasCount]);

  // ============================================================
  // Estado de filtros por pares (estado, modalidad) para mercados
  // - FilterPanel: chips por estado (toggle todos los pares de ese estado)
  // - Legend:      items clickeables por modalidad (toggle pares individuales)
  // ============================================================
  const [selectedPairs, setSelectedPairs] = useState(
    () => new Set(ALL_MERCADO_PAIRS)
  );

  // Al cambiar de tab vuelve al default (todos los pares activos)
  useEffect(() => {
    setSelectedPairs(new Set(ALL_MERCADO_PAIRS));
  }, [activeTab]);

  const togglePair = useCallback((pairKey) => {
    setSelectedPairs((prev) => {
      const next = new Set(prev);
      if (next.has(pairKey)) next.delete(pairKey);
      else next.add(pairKey);
      return next;
    });
  }, []);

  const toggleEstado = useCallback((estadoId) => {
    const estado = COLORS_MERCADOS[estadoId];
    if (!estado) return;
    const estadoPairs = Object.keys(estado.modalidades || {}).map(
      (modId) => `${estadoId}-${modId}`
    );
    setSelectedPairs((prev) => {
      const allSelected = estadoPairs.every((p) => prev.has(p));
      const next = new Set(prev);
      estadoPairs.forEach((p) => {
        if (allSelected) next.delete(p);
        else next.add(p);
      });
      return next;
    });
  }, []);

  useEffect(() => {
    setFilteredData(null);
    // Reset del state de filtros raw (dropdowns, __text, __area) al cambiar
    // de tab. Los filtros no se comparten entre módulos — cada tab arranca
    // limpio. FilterPanel también resetea su propio internal state.
    setArcgisWhereClause({});
    // Inicializamos `selectedLayerIds` con los defaults de cada tab para evitar
    // que el badge muestre 0 mientras FilterPanel monta y propaga sus checkboxes
    // por primera vez. Sin esto, el counter quedaba en 0 por unos ms al cambiar
    // de tab antes de actualizar al valor correcto.
    setSelectedLayerIds(
      activeTab === 2
        ? PARQUES_DEFAULT_LAYERS
        : activeTab === 3
          ? ZONAS_DEFAULT_LAYERS
          : []
    );
  }, [activeTab, PARQUES_DEFAULT_LAYERS, ZONAS_DEFAULT_LAYERS]);

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
    if (!isFilterOpen) {
      setIsBuscarOpen(false);
    }
  };

  const toggleBuscar = () => {
    setIsBuscarOpen(!isBuscarOpen);
    if (!isBuscarOpen) {
      setIsFilterOpen(false);
    }
  };

  const handleFilterChange = useCallback((filteredData) => {
    setFilteredData(filteredData);
  }, []);

  const handleSearchChange = useCallback((filteredData) => {
    setFilteredData(filteredData);
    setIsSearchMode(true);
  }, []);

  // FilterPanel emite cambios parciales de filtros (puede ser solo {__text}
  // desde el buscador, o {...dropdowns, __area} desde el panel). Mergeamos
  // contra el state previo para que coexistan. Cada ParqueSublayer/ZonaSublayer
  // construye su propio WHERE per-layer en base al state combinado.
  const handleArcgisFilterChange = useCallback((partial) => {
    setArcgisWhereClause((prev) => {
      const base =
        typeof prev === "object" && prev ? { ...prev } : {};
      return { ...base, ...(partial || {}) };
    });
  }, []);

  const handleCentroideChange = useCallback((centroide) => {
    setCentroide(centroide);
  }, []);

  // Click en el mapa (incluye clicks que vienen de los markers, ya que el
  // evento `click` de Leaflet burbujea desde el marker hasta el map).
  // No cerramos paneles aquí: el FilterPanel solo debe colapsarse cuando el
  // usuario lo hace explícitamente desde su handle, o cuando un modal de
  // detalle se abre (eso lo maneja la clase `has-detail-open` en el body
  // desde MapWithOutSidebar). Tampoco tocamos el panel de búsqueda.
  const handleMapClick = () => {};

  // useCallback estable para no romper la memoización de MapWithOutSidebar
  // (que recibe este callback como prop) — sin esto cada count update de
  // parques/zonas en Map.js creaba una ref nueva y propagaba re-renders.
  const handleDetailModalClose = useCallback(() => {
    setIsFilterOpen(true);
  }, []);
  return (
    <div style={{ height: "100vh", width: "100%", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <MapWithOutSidebar
          filteredData={filteredData}
          activeTab={activeTab}
          arcgisWhereClause={arcgisWhereClause}
          centroide={centroide}
          layerId={selectedLayerIds}
          onMapClick={handleMapClick}
          selectedFilters={selectedFilters}
          isSearchMode={isSearchMode}
          theme={theme}
          selectedPairs={selectedPairs}
          togglePair={togglePair}
          onDetailModalClose={handleDetailModalClose}
        />
      </div>
      <Header />
      <HeaderAbove
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        theme={theme}
        setTheme={setTheme}
        tabCounts={tabCounts}
      />
      {(
        <FilterPanel
          isOpen={isFilterOpen}
          activeTab={activeTab}
          onToggle={toggleFilter}
          onFilterChange={handleFilterChange}
          onArcgisFilterChange={handleArcgisFilterChange}
          onCentroideChange={handleCentroideChange}
          onLayerChange={setSelectedLayerIds}
          layerId={selectedLayerIds}
          count={tabCounts[activeTab]}
          selectedPairs={selectedPairs}
          toggleEstado={toggleEstado}
        />
      )}
      {/*
        <BuscarPanel
          isOpen={isBuscarOpen}
          onToggle={toggleBuscar}
          onSearchChange={handleSearchChange}
          activeTab={activeTab}
          onFiltersChange={setSelectedFilters}
        />
      */}
    </div>
  );
};

export default Map;
